import { DotsVertical, Edit05, Trash02 } from "@untitledui/icons";
import { IconButton } from "@/components/Atoms/IconButton/IconButton";
import { Dropdown } from "@/components/base/dropdown/dropdown";
import { useAppTranslation } from "@/hooks/use-app-translation";
import type { AdminFormI18nPaths } from "../../admin-form-editor-i18n-paths";
import type { FormRule } from "../../types/form-rule-types";
import { FormRuleToggle } from "./form-rule-toggle";

export interface FormRuleCardProps {
	rule: FormRule;
	paths: AdminFormI18nPaths;
	onEdit: (id: string) => void;
	onDelete: (id: string) => void;
	onToggleEnabled: (id: string, enabled: boolean) => void;
	/** When true, the overflow menu is hidden and the toggle is disabled. */
	isReadOnly?: boolean;
}

/**
 * One form-rule row: name, condition and action counts, an Enabled switch,
 * and an overflow menu for edit and delete.
 */
export function FormRuleCard({
	rule,
	paths,
	onEdit,
	onDelete,
	onToggleEnabled,
	isReadOnly,
}: FormRuleCardProps) {
	const { t } = useAppTranslation();
	const conditionCount = rule.conditions.length;
	const actionCount = rule.actions.length;
	const summary = `${t(conditionCount === 1 ? paths.formRules.conditionCount_one : paths.formRules.conditionCount, { count: conditionCount })} · ${t(actionCount === 1 ? paths.formRules.actionCount_one : paths.formRules.actionCount, { count: actionCount })}`;

	return (
		<li
			className="flex min-h-[70px] items-center gap-4 rounded-xl bg-primary p-4 ring-1 ring-gray-300 ring-inset"
			data-testid={`form-rule-card-${rule.id}`}
		>
			<div className="min-w-0 flex-1">
				<p className="truncate text-sm font-semibold leading-5 text-primary">
					{rule.name}
				</p>
				<p className="truncate text-xs leading-[18px] font-medium text-tertiary">
					{summary}
				</p>
			</div>
			<div className="flex shrink-0 items-center gap-2">
				<span className="text-xs leading-[18px] font-medium text-tertiary">
					{t(paths.formRules.statusEnabled)}
				</span>
				<FormRuleToggle
					isSelected={rule.enabled ?? true}
					onChange={(value) => {
						onToggleEnabled(rule.id, value);
					}}
					aria-label={t(paths.formRules.toggleEnable)}
					isDisabled={isReadOnly}
				/>
				{isReadOnly ? null : (
					<Dropdown.Root>
						<IconButton
							hierarchy="link"
							size="sm"
							shape="square"
							icon={DotsVertical}
							aria-label={t("common.tableActions.options")}
							data-testid={`form-rule-card-menu-${rule.id}`}
							className="*:data-icon:text-fg-quaternary! hover:*:data-icon:text-fg-quaternary!"
						/>
						<Dropdown.Popover>
							<Dropdown.Menu selectionMode="none">
								<Dropdown.Item
									id="edit"
									label={t(paths.formRules.editRule)}
									icon={Edit05}
									onAction={() => {
										onEdit(rule.id);
									}}
								/>
								<Dropdown.Item
									id="delete"
									label={t(paths.formRules.deleteRule)}
									icon={Trash02}
									onAction={() => {
										onDelete(rule.id);
									}}
								/>
							</Dropdown.Menu>
						</Dropdown.Popover>
					</Dropdown.Root>
				)}
			</div>
		</li>
	);
}
